// „Meine Wallets" — Adress-Registry pro Konto (users/{uid}/wallets).
//
// Zweck: Der User hinterlegt seine eigenen Wallet-Adressen samt Name (z.B. „Ledger",
// „MetaMask") und Chain. Beim Import einer dieser bekannten Adressen ordnet die Engine
// das Label automatisch zu — kein „→ ETH" mehr, kein manuelles Nachbenennen.
//
// Privacy: rein die eigenen Daten des Users (Adressen sind on-chain ohnehin öffentlich),
// kein Dritter, Opt-in. Konsistent mit privacy-first / kein Tracking.

import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from '../lib/fsCompat';
import { db } from '../firebase';

/** Adress-Vergleich: lowercase + trim (für „kenne ich diese Adresse?"). */
export function normalizeAddress(addr) {
  return String(addr || '').trim().toLowerCase();
}

/** Alle hinterlegten Wallets des Users. */
export async function listWallets(uid) {
  if (!uid) return [];
  const snap = await getDocs(collection(db, 'users', uid, 'wallets'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
}

/**
 * Neue Wallet anlegen. Gibt die neue Doc-ID zurück.
 * `source`: 'manual' (User-Eingabe) | 'auto' (beim Import selbst angelegt).
 * `isOwn` defaultet auf true — alles unter „Meine Wallets" IST per Definition eine
 * eigene Wallet (der frühere Toggle war bedeutungslos: die Eigen-Transfer-Erkennung
 * nutzt ohnehin jede registrierte Adresse).
 */
export async function addWallet(uid, { label, chain, address, source = 'manual', provider = null }) {
  if (!uid) throw new Error('not signed in');
  const cleanLabel = String(label || '').trim().slice(0, 40);
  const cleanAddr = String(address || '').trim().slice(0, 140);
  if (!cleanLabel || !chain || !cleanAddr) throw new Error('label, chain und address sind Pflicht');
  const cleanProvider = provider ? String(provider).trim().slice(0, 40) : null;
  const ref = await addDoc(collection(db, 'users', uid, 'wallets'), {
    label: cleanLabel,
    chain,
    address: cleanAddr,
    addressLc: normalizeAddress(cleanAddr),
    isOwn: true,
    source,
    // Anbieter aus der IMPORT-QUELLE (Ledger-CSV → „Ledger", Connect-Flow → gewählte
    // Marke). Die Wahrheit für die Gruppierung in „Meine Wallets" — kein Raten.
    ...(cleanProvider ? { provider: cleanProvider } : {}),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * Eigene Wallets idempotent registrieren — beim Import (Solana-Sync, Ledger-CSV)
 * legt das System die Adressen des Kunden selbst an, damit die Eigen-Transfer-
 * Erkennung (classifyOwnWalletTransfers) ohne manuelles Zutun greift. Bereits
 * bekannte Adressen (normalisiert) werden übersprungen. Fehlertolerant: ein
 * fehlgeschlagener Einzeleintrag stoppt den Import nicht.
 *
 * @param {string} uid
 * @param {Array<{label:string, chain:string, address:string}>} entries
 * @returns {Promise<Array>} die neu angelegten Einträge
 */
export async function ensureOwnWallets(uid, entries) {
  if (!uid || !Array.isArray(entries) || !entries.length) return [];
  let existing = [];
  try { existing = await listWallets(uid); } catch { return []; }
  const byAddr = new Map(existing.map(w => [w.addressLc || normalizeAddress(w.address), w]));
  const added = [];
  for (const e of entries) {
    const lc = normalizeAddress(e?.address);
    if (!lc) continue;
    const known = byAddr.get(lc);
    if (known) {
      // Bekannte Adresse: fehlenden Anbieter aus der Quelle NACHTRAGEN (Backfill für
      // Bestandsdaten ohne Doppel-Anlage). Ein Re-Import der Ledger-CSV tagt so alle
      // bereits angelegten Ledger-Wallets korrekt mit provider:'Ledger'.
      if (e?.provider && !known.provider) {
        try {
          await updateDoc(doc(db, 'users', uid, 'wallets', known.id), { provider: String(e.provider).trim().slice(0, 40) });
          known.provider = e.provider;
        } catch { /* Backfill best-effort */ }
      }
      continue;
    }
    if (!e?.label || !e?.chain) continue;
    byAddr.set(lc, { ...e }); // sofort markieren → keine Doppel-Anlage bei Duplikaten in `entries`
    try {
      const id = await addWallet(uid, { ...e, source: 'auto' });
      added.push({ id, ...e });
    } catch { /* einzelner Fehlschlag ignorieren */ }
  }
  return added;
}

/** Wallet entfernen. */
export async function deleteWallet(uid, id) {
  if (!uid || !id) return;
  await deleteDoc(doc(db, 'users', uid, 'wallets', id));
}

/**
 * Watch-Flags einer hinterlegten Wallet setzen.
 *  - autoWatch: täglich auf neue Bewegungen prüfen (Discovery-Loop).
 *  - isOwn:     eigene Wallet → Transfers dorthin sind kein Verkauf (kein
 *               steuerliches Event, INVARIANT 4). Reines Flag — die eigentliche
 *               Klassifikation passiert weiter im Read-Layer.
 * Der Cursor (Stand der zuletzt gesehenen TX) wird ausschließlich server-seitig
 * vom Cron geschrieben, NICHT hier.
 */
export async function setWalletWatch(uid, id, { autoWatch, isOwn } = {}) {
  if (!uid || !id) return;
  const patch = {};
  if (autoWatch !== undefined) patch.autoWatch = !!autoWatch;
  if (isOwn !== undefined) patch.isOwn = !!isOwn;
  if (Object.keys(patch).length === 0) return;
  await updateDoc(doc(db, 'users', uid, 'wallets', id), patch);
}

/** Nur die überwachten Wallets (autoWatch === true). */
export async function listWatchedWallets(uid) {
  return (await listWallets(uid)).filter(w => w.autoWatch === true);
}

/** Bekannte Wallet zu einer Adresse finden (für Auto-Label). */
export function matchWallet(wallets, address) {
  const lc = normalizeAddress(address);
  if (!lc) return null;
  return (wallets || []).find(w => normalizeAddress(w.address) === lc) || null;
}
